import { useEffect, useRef } from 'react'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { selectMatterById } from '@/matter/store/selectors'
import { canEditMatterSettings } from '@/matter/utils/matter-permissions'
import { useAppSelector } from '@/store/store-hooks'
import { useMatterExternalSyncStatus } from '@/user_files/external-storage/hooks/useMatterExternalSyncStatus'
import { ExternalStorageFooterVariant, ExternalStorageMenuState } from '@/user_files/external-storage/menu-state'
import { PROVIDER_ICON_COMPONENTS, PROVIDER_META } from '@/user_files/external-storage/ProviderMeta'

type UseExternalStorageMenuStatusParams = {
  matterId: string | null | undefined
  menuOpen: boolean
  enabled?: boolean
}

type ExternalStorageActionKind = 'open-files' | 'connect-folder'

export function useExternalStorageMenuStatus({ matterId, menuOpen, enabled = true }: UseExternalStorageMenuStatusParams) {
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const matter = useAppSelector((state) => selectMatterById(state, { matterId }))
  const externalSyncStatus = useMatterExternalSyncStatus({
    matterId,
    enabled: enabled && menuOpen && driveSyncEnabled,
  })
  const { error, loading: syncLoading, reload } = externalSyncStatus
  const retriedErrorForOpenRef = useRef(false)

  useEffect(() => {
    if (!menuOpen) {
      retriedErrorForOpenRef.current = false
      return
    }
    if (!enabled || !driveSyncEnabled || retriedErrorForOpenRef.current || !error || syncLoading) return

    retriedErrorForOpenRef.current = true
    reload()
  }, [driveSyncEnabled, enabled, error, syncLoading, menuOpen, reload])

  const displayProvider = externalSyncStatus.displayProvider
  const connectedProviderMeta = displayProvider ? PROVIDER_META[displayProvider] : null
  const ConnectedProviderIcon = displayProvider ? PROVIDER_ICON_COMPONENTS[displayProvider] : undefined
  const loading = !!matterId && externalSyncStatus.loading
  const canConnectMatterFolder = canEditMatterSettings(matter)
  const canShowActionForSyncState = externalSyncStatus.hasActiveMatterSync || canConnectMatterFolder
  const showMenuItem =
    enabled &&
    driveSyncEnabled &&
    !!matterId &&
    !loading &&
    !externalSyncStatus.error &&
    !!connectedProviderMeta &&
    !!ConnectedProviderIcon &&
    canShowActionForSyncState
  const actionKind: ExternalStorageActionKind | null = showMenuItem ? (externalSyncStatus.hasActiveMatterSync ? 'open-files' : 'connect-folder') : null

  // Footer swap: show the "connect your DMS" CTA only when the viewer can configure the matter,
  // drive-sync is on, we have a settled matter context, no active matter sync, and no DMS account
  // is connected at all. Anything else falls back to secure.
  const footerVariant: ExternalStorageFooterVariant =
    enabled &&
    driveSyncEnabled &&
    !!matterId &&
    canConnectMatterFolder &&
    !loading &&
    !externalSyncStatus.error &&
    !externalSyncStatus.hasConnectedAccount &&
    !externalSyncStatus.hasActiveMatterSync
      ? ExternalStorageFooterVariant.ConnectDms
      : ExternalStorageFooterVariant.Secure
  const menuState = showMenuItem
    ? ExternalStorageMenuState.ConnectedAccount
    : footerVariant === ExternalStorageFooterVariant.ConnectDms
    ? ExternalStorageMenuState.EnabledNoConnectedAccount
    : ExternalStorageMenuState.Disabled

  return {
    actionKind,
    connectedProviderMeta,
    ConnectedProviderIcon,
    loading,
    showMenuItem,
    footerVariant,
    menuState,
  }
}

export type ExternalStorageMenuStatus = ReturnType<typeof useExternalStorageMenuStatus>
